const NVIDIA_RESOURCE_PREFIX = 'nvidia.com/'
const GPU_MEMORY_RESOURCE = 'nvidia.com/gpumem'
const GPU_MEMORY_PERCENT_RESOURCE = 'nvidia.com/gpumem-percentage'
const GPU_CORES_RESOURCE = 'nvidia.com/gpucores'
const GPU_DEVICE_RESOURCE = 'nvidia.com/gpu'
const GPU_RESOURCE_KINDS = ['requests', 'limits']
const MIG_MEMORY_PROFILE = /^nvidia\.com\/mig-[^.]+\.(\d+(?:\.\d+)?)gb$/i
const EPHEMERAL_STORAGE_RESOURCE = 'ephemeral-storage'
// Quantities for these keys display in decimal GB, matching the application size selector.
const DECIMAL_GB_RESOURCES = new Set(['memory', EPHEMERAL_STORAGE_RESOURCE])

// Backend DateTime columns are UTC but may serialize without a timezone suffix.
export function recordedTime(value) {
    if (typeof value === 'number') return value
    if (typeof value !== 'string' || !value) return NaN
    const zoned = /(?:z|[+-]\d{2}:?\d{2})$/i.test(value)
    const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(value)
    return Date.parse(dateOnly ? `${value}T00:00:00Z` : zoned ? value : `${value}Z`)
}

export function formatAllocationTime(value) {
    const time = recordedTime(value)
    return Number.isFinite(time)
        ? new Date(time).toLocaleString('en-GB', { timeZone: 'UTC', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) + ' UTC'
        : 'Unknown'
}

function normalizeNodePool(value) {
    if (typeof value !== 'string' || !value || value === 'nv-app') return ''
    return value.startsWith('nv-app') ? value.slice(6) : value
}

function isStandardSession(session) {
    if (!session || (session.tcid !== null && session.tcid !== undefined)) return false
    return session.active_resource === 'ncu' || !normalizeNodePool(session.node_pool)
}

function intervalBounds(record) {
    if (!record) return null
    const start = recordedTime(record.start_time)
    const hasStop = record.stop_time !== null && record.stop_time !== undefined && record.stop_time !== ''
    const stop = hasStop ? recordedTime(record.stop_time) : Infinity
    return Number.isFinite(start) && !Number.isNaN(stop) && start < stop ? { start, stop } : null
}

function windowBounds(start, end) {
    const from = recordedTime(start)
    const until = recordedTime(end)
    return Number.isFinite(from) && Number.isFinite(until) && from < until ? { start: from, end: until } : null
}

// Kubernetes quantities remain in their original persisted form until display.
// Memory is converted to decimal GB, matching the application size selector.
function resourceQuantity(value, resource) {
    if (value === null || value === undefined || value === '') return null
    const match = String(value).match(/^([+]?(?:\d+(?:\.\d*)?|\.\d+)(?:e[+-]?\d+)?)([a-zA-Z]*)$/i)
    if (!match) return null
    const multipliers = {
        '': 1,
        n: 1e-9,
        u: 1e-6,
        m: 1e-3,
        k: 1e3,
        K: 1e3,
        M: 1e6,
        G: 1e9,
        T: 1e12,
        P: 1e15,
        E: 1e18,
        Ki: 2 ** 10,
        Mi: 2 ** 20,
        Gi: 2 ** 30,
        Ti: 2 ** 40,
        Pi: 2 ** 50,
        Ei: 2 ** 60
    }
    const multiplier = multipliers[match[2]]
    if (multiplier === undefined) return null
    const quantity = (Number(match[1]) * multiplier) / (DECIMAL_GB_RESOURCES.has(resource) ? 1e9 : 1)
    return Number.isFinite(quantity) && quantity >= 0 ? quantity : null
}

// HAMI's nvidia.com/gpumem is MiB when unitless. Explicit byte quantities use
// their stated unit and are converted to decimal GB for display.
function gpuMemoryQuantity(value) {
    if (value === null || value === undefined || value === '') return null
    const match = String(value).match(/^([+]?(?:\d+(?:\.\d*)?|\.\d+)(?:e[+-]?\d+)?)([a-zA-Z]*)$/i)
    if (!match) return null
    const unit = match[2].toLowerCase()
    const byteMultipliers = {
        b: 1,
        kb: 1e3,
        mb: 1e6,
        gb: 1e9,
        tb: 1e12,
        pb: 1e15,
        eb: 1e18,
        kib: 2 ** 10,
        mib: 2 ** 20,
        gib: 2 ** 30,
        tib: 2 ** 40,
        pib: 2 ** 50,
        eib: 2 ** 60
    }
    if (!unit) {
        const quantity = (Number(match[1]) * 2 ** 20) / 1e9
        return Number.isFinite(quantity) && quantity >= 0 ? quantity : null
    }
    if (byteMultipliers[unit] !== undefined) {
        const quantity = (Number(match[1]) * byteMultipliers[unit]) / 1e9
        return Number.isFinite(quantity) && quantity >= 0 ? quantity : null
    }
    return resourceQuantity(value, 'memory')
}

function resourceMap(container, kind) {
    const resources = container?.[kind]
    return resources && typeof resources === 'object' && !Array.isArray(resources) ? resources : null
}

function measurement(value, status) {
    return { value, status }
}

// Combine per-container or per-record measurement flags into one measurement.
// `known` means at least one numeric value exists; `missing` means at least one
// contribution had no recorded value (or an incomplete one); `uncapped` means a
// recorded limits map omitted this key, so the value is a lower bound.
function measurementFromFlags(known, missing, uncapped, value, kind) {
    if (kind === 'requests') {
        if (!missing) return measurement(known ? value : 0, 'exact')
        return known ? measurement(value, 'partial') : measurement(null, 'unrecorded')
    }
    if (!missing && !uncapped) return measurement(known ? value : 0, 'exact')
    if (missing && known) return measurement(value, 'partial')
    if (missing) return measurement(null, uncapped ? 'uncapped' : 'unrecorded')
    return measurement(known ? value : null, 'uncapped')
}

function combineContributions(contributions, kind) {
    if (contributions.length === 0) return measurement(null, 'unrecorded')
    let total = 0
    let known = false
    let missing = false
    let uncapped = false
    for (const contribution of contributions) {
        if (contribution.value !== null) {
            total = cleanSum(total + contribution.value)
            known = true
        }
        if (contribution.status === 'uncapped') uncapped = true
        else if (contribution.status !== 'exact') missing = true
    }
    return measurementFromFlags(known, missing, uncapped, total, kind)
}

function parseAmount(value, key) {
    return key === GPU_MEMORY_RESOURCE ? gpuMemoryQuantity(value) : resourceQuantity(value, key)
}

function containerMeasurement(container, kind, key) {
    const resources = kind === 'requests' ? container.requests : container.limits
    const gpuKey = key.startsWith(NVIDIA_RESOURCE_PREFIX)
    if (!resources) return measurement(null, 'unrecorded')
    if (Object.prototype.hasOwnProperty.call(resources, key)) {
        const amount = parseAmount(resources[key], key)
        if (amount === null) return measurement(null, 'unrecorded')
        if (key === GPU_CORES_RESOURCE || key === GPU_MEMORY_PERCENT_RESOURCE) {
            const devices = parseAmount(resources[GPU_DEVICE_RESOURCE], GPU_DEVICE_RESOURCE)
            if (devices === null) return measurement(null, 'unrecorded')
            return measurement(amount * devices, 'exact')
        }
        return measurement(amount, 'exact')
    }
    // An explicit limits map without this key records "no explicit limit"; the
    // recorded request is the known minimum contribution until the record ends.
    if (kind === 'limits' && !gpuKey) return measurement(containerMeasurement(container, 'requests', key).value, 'uncapped')
    if (key === GPU_MEMORY_RESOURCE && Object.prototype.hasOwnProperty.call(resources, GPU_MEMORY_PERCENT_RESOURCE)) {
        const percentage = parseAmount(resources[GPU_MEMORY_PERCENT_RESOURCE], GPU_MEMORY_PERCENT_RESOURCE)
        if (percentage === null || percentage > 0) return measurement(null, 'unrecorded')
    }
    const counterpart = kind === 'requests' ? container.limits : container.requests
    if (
        key === GPU_MEMORY_PERCENT_RESOURCE &&
        (Object.prototype.hasOwnProperty.call(resources, GPU_MEMORY_RESOURCE) ||
            (counterpart && Object.prototype.hasOwnProperty.call(counterpart, GPU_MEMORY_RESOURCE)))
    ) {
        const absolute = parseAmount(resources[GPU_MEMORY_RESOURCE], GPU_MEMORY_RESOURCE)
        if (absolute === null || absolute > 0) return measurement(null, 'unrecorded')
    }
    if (counterpart && Object.prototype.hasOwnProperty.call(counterpart, key)) return measurement(null, 'unrecorded')
    if (gpuKey) return counterpart ? measurement(0, 'exact') : measurement(null, 'unrecorded')
    return measurement(null, 'unrecorded')
}

// Session resource_config is parsed once per session record; summaries and the
// aggregate sweep reuse the parsed containers instead of re-reading JSON.
function readSessionResources(session) {
    let containers = session?.resource_config
    try {
        if (typeof containers === 'string') containers = JSON.parse(containers)
    } catch (_) {
        containers = null
    }
    if (!Array.isArray(containers) || containers.length === 0) {
        return { available: false, containers: [], gpuKeys: new Set(), summaries: new Map() }
    }
    const parsed = {
        available: true,
        containers: containers.map((container, index) => ({
            name: typeof container?.name === 'string' && container.name ? container.name : `Container ${index + 1} (name not recorded)`,
            role: container?.role === 'main' || container?.role === 'sidecar' ? container.role : 'unrecorded',
            requests: resourceMap(container, 'requests'),
            limits: resourceMap(container, 'limits')
        })),
        gpuKeys: new Set(),
        summaries: new Map()
    }
    for (const container of parsed.containers)
        for (const kind of GPU_RESOURCE_KINDS) {
            const resources = kind === 'requests' ? container.requests : container.limits
            if (!resources) continue
            for (const key of Object.keys(resources)) if (key.startsWith(NVIDIA_RESOURCE_PREFIX)) parsed.gpuKeys.add(key)
        }
    return parsed
}

function buildSessionSummary(resources, key) {
    if (!resources.available) return { requests: measurement(null, 'unrecorded'), limits: measurement(null, 'unrecorded'), containers: [] }
    const containers = resources.containers.map(container => ({
        name: container.name,
        role: container.role,
        requests: containerMeasurement(container, 'requests', key),
        limits: containerMeasurement(container, 'limits', key)
    }))
    return {
        requests: combineContributions(
            containers.map(container => container.requests),
            'requests'
        ),
        limits: combineContributions(
            containers.map(container => container.limits),
            'limits'
        ),
        containers
    }
}

function sessionSummary(resources, key) {
    if (!resources.summaries.has(key)) resources.summaries.set(key, buildSessionSummary(resources, key))
    return resources.summaries.get(key)
}

/**
 * Recorded per-session requests/limits for one resource key. Values absent or
 * unparsable stay unrecorded or partial instead of being guessed from the other
 * kind; `uncapped` means an explicit limits map omitted the key.
 */
export function sessionResourceSummary(session, key) {
    return sessionSummary(readSessionResources(session), typeof key === 'string' ? key : '')
}

function allocateMeasurement(source, resource) {
    const multiplier = resource?.multiplier
    if (source.value === null || multiplier === null || multiplier === undefined) return source
    return { value: source.value * multiplier, status: source.status }
}

/**
 * Allocation-scoped view of a session summary: the node's normalization
 * multiplier applies to every recorded value. Missing normalization capacity
 * falls back to the raw recorded units rather than dropping numeric data.
 */
export function sessionAllocationSummary(session, resource) {
    const summary = sessionResourceSummary(session, resource?.key)
    return {
        requests: allocateMeasurement(summary.requests, resource),
        limits: allocateMeasurement(summary.limits, resource),
        containers: summary.containers.map(container => ({
            name: container.name,
            role: container.role,
            requests: allocateMeasurement(container.requests, resource),
            limits: allocateMeasurement(container.limits, resource)
        }))
    }
}

const measurementNumberFormat = new Intl.NumberFormat('en', { maximumFractionDigits: 2 })

function measurementText(value) {
    return measurementNumberFormat.format(value)
}

/**
 * Display text for a {value, status} measurement. Partial and uncapped values
 * are known lower bounds, so they carry the ≥ prefix; missing data is stated
 * explicitly instead of rendering a placeholder like "Unknown".
 */
export function formatResourceMeasurement(measurement, unit = '') {
    const suffix = typeof unit === 'string' && unit ? ` ${unit}` : ''
    const source = measurement && typeof measurement === 'object' ? measurement : null
    const value = source?.value
    const status = source?.status
    if (typeof value === 'number' && Number.isFinite(value)) {
        const text = `${measurementText(value)}${suffix}`
        return status === 'partial' || status === 'uncapped' ? `≥ ${text}` : text
    }
    if (status === 'uncapped') return 'No explicit limit'
    return 'Not recorded'
}

/**
 * Caveat text for an inexact measurement; `kind` is 'requests' or 'limits'.
 * Exact measurements need no warning, and container-level views can add their
 * own context for a single uncapped container.
 */
export function resourceMeasurementWarning(measurement, kind) {
    const status = measurement?.status
    if (!status || status === 'exact') return null
    if (kind === 'requests') {
        if (status === 'partial') return 'Additional reservations may apply'
        if (status === 'unrecorded') return 'Reservations were not recorded'
        return null
    }
    if (kind === 'limits') {
        if (status === 'partial') return 'Total could be higher; some limits were not recorded'
        if (status === 'uncapped') return 'Could be higher; one or more containers have no explicit limit'
        if (status === 'unrecorded') return 'Limits were not recorded'
        return null
    }
    return null
}

function quantityFromNode(node, field, key) {
    const value = node?.meta_data?.[field]?.[key]
    return key === GPU_MEMORY_RESOURCE ? gpuMemoryQuantity(value) : resourceQuantity(value, key)
}

function migProfileMemoryGb(key) {
    const match = typeof key === 'string' ? key.match(MIG_MEMORY_PROFILE) : null
    if (!match) return null
    const amount = Number(match[1])
    return Number.isFinite(amount) && amount > 0 ? amount : null
}

function resourcePoolMap(resourcePools) {
    const pools = new Map()
    for (const pool of resourcePools) {
        if (pool?.rpid !== null && pool?.rpid !== undefined && !pools.has(String(pool.rpid))) pools.set(String(pool.rpid), pool)
    }
    return pools
}

function nodePoolMaps(nodePools) {
    const byShortId = new Map()
    const byNid = new Map()
    for (const pool of nodePools) {
        if (typeof pool?.short_id === 'string' && !byShortId.has(normalizeNodePool(pool.short_id))) byShortId.set(normalizeNodePool(pool.short_id), pool)
        if (pool?.nid !== null && pool?.nid !== undefined && !byNid.has(String(pool.nid))) byNid.set(String(pool.nid), pool)
    }
    return { byShortId, byNid }
}

function sessionCluster(session, poolByShortId) {
    if (session.tcid !== undefined) return session.tcid
    if (isStandardSession(session)) return null
    return poolByShortId.get(normalizeNodePool(session.node_pool))?.tcid
}

function identifierKey(value) {
    return value === null || value === undefined ? null : String(value)
}

function workerKey(tcid, nodeName) {
    return `${identifierKey(tcid) ?? '<null>'}\u0000${nodeName}`
}

function buildWorkerIndex(nodeHistory) {
    const buckets = new Map()
    for (const node of nodeHistory) {
        const bounds = intervalBounds(node)
        if (!bounds || typeof node.node_name !== 'string' || !node.node_name) continue
        const entry = { node, start: bounds.start, stop: bounds.stop }
        const key = workerKey(node.tcid, node.node_name)
        if (!buckets.has(key)) buckets.set(key, [])
        buckets.get(key).push(entry)
    }
    for (const [key, entries] of buckets) {
        entries.sort((left, right) => left.start - right.start || left.stop - right.stop)
        const prefixMaxStop = []
        let maximumStop = -Infinity
        for (const entry of entries) {
            maximumStop = Math.max(maximumStop, entry.stop)
            prefixMaxStop.push(maximumStop)
        }
        buckets.set(key, { entries, prefixMaxStop })
    }
    return buckets
}

function firstPossibleInterval(prefixMaxStop, time, end) {
    let low = 0
    let high = end
    while (low < high) {
        const middle = (low + high) >> 1
        if (prefixMaxStop[middle] > time) high = middle
        else low = middle + 1
    }
    return low
}

function overlappingWorkerEntries(index, start, end) {
    if (!index || start >= end) return []
    const { entries, prefixMaxStop } = index
    let low = 0
    let high = entries.length
    while (low < high) {
        const middle = (low + high) >> 1
        if (entries[middle].start < end) low = middle + 1
        else high = middle
    }
    const stop = low
    const first = firstPossibleInterval(prefixMaxStop, start, stop)
    const overlapping = []
    for (let position = first; position < stop; position++) {
        const entry = entries[position]
        if (entry.stop > start && entry.start < end) overlapping.push(entry)
    }
    return overlapping
}

function sameIdentifier(left, right) {
    return left === right || (left !== null && left !== undefined && right !== null && right !== undefined && String(left) === String(right))
}

function nodeMatchesSessionPool(node, session, cluster, pool) {
    const sessionShortId = normalizeNodePool(session.node_pool)
    if (isStandardSession(session))
        return node.nid === null || node.nid === undefined
            ? node.tcid === null || node.tcid === undefined
                ? !normalizeNodePool(node.node_pool)
                : false
            : false

    if (pool && pool.tcid !== undefined && !sameIdentifier(pool.tcid, cluster)) return false
    if (!sessionShortId) return session.tcid !== null && session.tcid !== undefined
    const nodeShortId = normalizeNodePool(node.node_pool)
    if (pool) {
        if (node.nid !== null && node.nid !== undefined) return sameIdentifier(node.nid, pool.nid)
        return nodeShortId === normalizeNodePool(pool.short_id)
    }
    return nodeShortId === sessionShortId
}

function resolveSessionNodes(workerIndex, session, start, end, cluster, pool) {
    if (cluster === undefined || !session.worker_node_name) return []
    const index = workerIndex.get(workerKey(cluster, session.worker_node_name))
    const entries = overlappingWorkerEntries(index, start, end).filter(entry => nodeMatchesSessionPool(entry.node, session, cluster, pool))
    let previousStop = start
    for (const entry of entries) {
        if (Math.max(start, entry.start) < previousStop) return []
        previousStop = Math.min(end, entry.stop)
    }
    return entries
}

function nodePoolFor(node, sessions, poolMaps) {
    if (node.nid !== null && node.nid !== undefined) {
        const pool = poolMaps.byNid.get(String(node.nid))
        if (pool) return pool
    }
    const nodeShortId = normalizeNodePool(node.node_pool)
    if (nodeShortId) {
        const pool = poolMaps.byShortId.get(nodeShortId)
        if (pool) return pool
    }
    for (const session of sessions) {
        const sessionShortId = normalizeNodePool(session.node_pool)
        if (!sessionShortId) continue
        const pool = poolMaps.byShortId.get(sessionShortId)
        if (pool) return pool
    }
    return null
}

function nodeFamily(node, pool, sessions) {
    if ((node.tcid !== null && node.tcid !== undefined) || sessions.some(session => session.tcid !== null && session.tcid !== undefined)) return 'integrations'
    if (sessions.length === 0) {
        // Idle cached lifetimes have no sessions to classify; the recorded node
        // pool decides so azure workers are not left in the platform family.
        const cloud = pool?.cloud
        if (cloud !== null && cloud !== undefined) return String(cloud).toLowerCase() === 'azure' ? 'elastic' : 'integrations'
        const standard = (node.nid === null || node.nid === undefined) && !normalizeNodePool(node.node_pool)
        return standard ? 'standard' : 'integrations'
    }
    if (sessions.every(isStandardSession)) return 'standard'
    const cloud = pool?.cloud || sessions.find(session => session.cloud)?.cloud
    return cloud?.toLowerCase() === 'azure' ? 'elastic' : 'integrations'
}

const STANDARD_POOL_KEY = 'standard'
const STANDARD_POOL_LABEL = 'Standard'

function clusterScopeToken(cluster) {
    return cluster === null || cluster === undefined ? 'tc:-' : `tc:${identifierKey(cluster)}`
}

function poolScopeToken(pool) {
    if (pool.nid !== null && pool.nid !== undefined) return `nid:${identifierKey(pool.nid)}`
    return `short:${normalizeNodePool(pool.short_id) || identifierKey(pool.short_id) || 'unrecorded'}`
}

function nodeScopeToken(node) {
    if (node.nhid !== null && node.nhid !== undefined) return `nhid:${identifierKey(node.nhid)}`
    return `node:${identifierKey(node.node_name) || 'unnamed'}@${identifierKey(node.start_time)}`
}

/**
 * Stable pool identity from cluster + node-pool metadata: `nid` resolved
 * through node_pools first, then the normalized short id. The platform Standard
 * pool is the logical bucket with no cluster and no recorded node pool; any
 * other unresolved pool keeps the cluster scope, and without a cluster each
 * lifetime stays its own bucket so unrelated nodes or clusters never merge.
 */
function nodePoolIdentity(node, pool, sessions) {
    let cluster = node.tcid
    if (cluster === null || cluster === undefined) cluster = pool?.tcid
    if ((cluster === null || cluster === undefined) && sessions.length)
        cluster = sessions.find(session => session.tcid !== null && session.tcid !== undefined)?.tcid
    const clusterToken = clusterScopeToken(cluster)
    if (pool) return { key: `${clusterToken}|${poolScopeToken(pool)}`, label: pool.long_id || pool.short_id || `Node pool ${pool.nid}` }
    const shortId = normalizeNodePool(node.node_pool)
    if (node.nid !== null && node.nid !== undefined) return { key: `${clusterToken}|nid:${identifierKey(node.nid)}`, label: shortId || `Node pool ${node.nid}` }
    if (shortId) {
        const scope = cluster === null || cluster === undefined ? `node:${nodeScopeToken(node)}` : clusterToken
        return { key: `${scope}|short:${shortId}`, label: shortId }
    }
    if (cluster === null || cluster === undefined) return { key: STANDARD_POOL_KEY, label: STANDARD_POOL_LABEL }
    return { key: `${clusterToken}|unrecorded|node:${nodeScopeToken(node)}`, label: 'Node pool not recorded' }
}

function nodePoolShortIdIndex(nodePools) {
    const index = new Map()
    for (const pool of nodePools) {
        const shortId = normalizeNodePool(pool.short_id)
        if (!shortId) continue
        if (!index.has(shortId)) index.set(shortId, [])
        index.get(shortId).push(pool)
    }
    return index
}

// An unplaced session is only attributed to a pool its recorded node pool
// names unambiguously: exactly one metadata record matches the cluster, or the
// cluster plus short id identifies one stable pool key. Ambiguous or
// unrecorded identities stay unattributed instead of guessing placement.
function sessionPoolIdentity(session, shortIdIndex) {
    if (isStandardSession(session)) return { key: STANDARD_POOL_KEY, label: STANDARD_POOL_LABEL, family: 'standard', tcid: null, clusterName: null }
    const shortId = normalizeNodePool(session.node_pool)
    if (!shortId) return null
    const candidates = shortIdIndex.get(shortId) || []
    const scoped =
        session.tcid === null || session.tcid === undefined
            ? candidates
            : candidates.filter(pool => pool.tcid === null || pool.tcid === undefined || sameIdentifier(pool.tcid, session.tcid))
    if (scoped.length > 1) return null
    if (scoped.length === 1) {
        const pool = scoped[0]
        const cluster = session.tcid !== null && session.tcid !== undefined ? session.tcid : pool.tcid
        const cloud = typeof pool.cloud === 'string' ? pool.cloud.toLowerCase() : ''
        return {
            key: `${clusterScopeToken(cluster)}|${poolScopeToken(pool)}`,
            label: pool.long_id || pool.short_id || `Node pool ${pool.nid}`,
            family: cluster === null || cluster === undefined ? (cloud === 'azure' ? 'elastic' : 'integrations') : 'integrations',
            tcid: cluster,
            clusterName: pool.cluster_name || null
        }
    }
    if (session.tcid !== null && session.tcid !== undefined)
        return { key: `${clusterScopeToken(session.tcid)}|short:${shortId}`, label: shortId, family: 'integrations', tcid: session.tcid, clusterName: null }
    return null
}

function spaceLabelIndex(snapshot) {
    const labels = new Map()
    const spaces = Array.isArray(snapshot?.spaces) ? snapshot.spaces : []
    for (const space of spaces) {
        const sid = space?.sid
        if (sid === null || sid === undefined) continue
        if (typeof space.space_long_id === 'string' && space.space_long_id && !labels.has(identifierKey(sid)))
            labels.set(identifierKey(sid), space.space_long_id)
    }
    return labels
}

function resourcePoolName(session, poolsById) {
    if (session.rpid === null || session.rpid === undefined) return null
    const pool = poolsById.get(String(session.rpid))
    return pool?.long_id || pool?.name || `Resource pool ${session.rpid}`
}

function uniqueValues(values) {
    return [...new Set(values.filter(value => typeof value === 'string' && value))]
}

function nodeResourceKeys(node) {
    const keys = new Set()
    for (const field of ['allocatable', 'capacity']) {
        const resources = node?.meta_data?.[field]
        if (!resources || typeof resources !== 'object' || Array.isArray(resources)) continue
        for (const key of Object.keys(resources)) if (key.startsWith(NVIDIA_RESOURCE_PREFIX)) keys.add(key)
    }
    return keys
}

function isHamiNode(node, gpuKeys) {
    const labels = node?.meta_data?.labels
    return (
        labels?.['hami.io/managed'] === 'true' ||
        gpuKeys.has(GPU_MEMORY_RESOURCE) ||
        gpuKeys.has(GPU_MEMORY_PERCENT_RESOURCE) ||
        gpuKeys.has(GPU_CORES_RESOURCE)
    )
}

function gpuResourcePresentation(key, hami) {
    if (key === 'nvidia.com/gpu') return hami ? { label: 'NVIDIA GPU slots', unit: 'virtual GPU slots' } : { label: 'NVIDIA GPUs', unit: 'physical GPUs' }
    if (key === GPU_MEMORY_RESOURCE)
        return {
            label: 'GPU memory (recorded absolute)',
            unit: 'GB',
            note: 'Recorded absolute GPU memory budgets, separate from percentage allocations and not measured GPU utilization.'
        }
    if (key === GPU_CORES_RESOURCE)
        return {
            label: 'GPU compute (% of available accelerator budget)',
            unit: '%',
            note: '100% is the reported aggregate accelerator allocation budget, not measured GPU utilization.'
        }
    if (key === GPU_MEMORY_PERCENT_RESOURCE)
        return {
            label: 'GPU memory allocation (% of available budget)',
            unit: '%',
            note: 'HAMI budgets provide 100 allocation units per physical GPU. Percentages are normalized to the node budget, not combined HBM bytes.'
        }
    if (key.startsWith('nvidia.com/mig-')) {
        const profile = key.slice('nvidia.com/'.length)
        const memory = migProfileMemoryGb(key)
        return {
            label: `MIG profile ${profile}${memory === null ? '' : ` · nominal VRAM ${memory} GB`}`,
            unit: 'MIG slices',
            ...(memory === null ? {} : { note: 'MIG profile memory is nominal per slice, not physical node HBM capacity.' })
        }
    }
    return { label: key, unit: 'resource units' }
}

function cleanSum(value) {
    return value < 0 && value > -1e-9 ? 0 : value
}

function addMeasurement(state, measurement, direction) {
    state.active += direction
    if (measurement.value !== null) {
        state.sum = cleanSum(state.sum + measurement.value * direction)
        state.known += direction
    }
    if (measurement.status === 'uncapped') state.uncapped += direction
    else if (measurement.status !== 'exact') state.missing += direction
}

function stateMeasurement(state, kind) {
    return measurementFromFlags(state.known > 0, state.missing > 0, state.uncapped > 0, cleanSum(state.sum), kind)
}

function resourceDefinitions(node, group) {
    const gpuKeys = nodeResourceKeys(node)
    for (const interval of group.intervals) for (const key of interval.resources.gpuKeys) gpuKeys.add(key)
    // A recorded absolute GPU memory budget only folds into the normalized
    // percentage lane when the node publishes a percentage allocation capacity.
    const normalization = quantityFromNode(node, 'allocatable', GPU_MEMORY_PERCENT_RESOURCE)
    const recordedAbsoluteMemory = group.intervals.some(interval => interval.resources.gpuKeys.has(GPU_MEMORY_RESOURCE))
    if (gpuKeys.has(GPU_MEMORY_RESOURCE) && normalization !== null && normalization > 0 && !recordedAbsoluteMemory) {
        gpuKeys.add(GPU_MEMORY_PERCENT_RESOURCE)
        gpuKeys.delete(GPU_MEMORY_RESOURCE)
    }
    const hami = isHamiNode(node, gpuKeys)
    const definitions = [
        { key: 'cpu', label: 'CPU', unit: 'vCPU' },
        { key: 'memory', label: 'Memory', unit: 'GB' },
        { key: EPHEMERAL_STORAGE_RESOURCE, label: 'Ephemeral storage', unit: 'GB' },
        ...[...gpuKeys].sort().map(key => ({ key, ...gpuResourcePresentation(key, hami) }))
    ]
    for (const definition of definitions) {
        definition.capacity = quantityFromNode(node, 'allocatable', definition.key)
        definition.totalCapacity = quantityFromNode(node, 'capacity', definition.key)
        if (definition.key === GPU_CORES_RESOURCE || definition.key === GPU_MEMORY_PERCENT_RESOURCE) {
            const normalizable = definition.capacity !== null && definition.capacity > 0
            // Without a normalization capacity the raw recorded units are kept
            // instead of dropping values that cannot be converted to a share.
            definition.multiplier = normalizable ? 100 / definition.capacity : 1
            definition.capacity = normalizable ? 100 : null
            if (normalizable) definition.totalCapacity = null
            else {
                definition.unit = 'GPU budget units'
                definition.label = definition.key === GPU_CORES_RESOURCE ? 'GPU compute (recorded budget)' : 'GPU memory (recorded budget)'
                definition.note = 'Normalization capacity was not recorded; these are raw allocation units, not percentages of node capacity.'
            }
        }
    }
    return definitions
}

function resourceMetricValue(resources, definition, kind) {
    return allocateMeasurement(sessionSummary(resources, definition.key)[kind], definition)
}

function recordWindowMeasurement(window, state, measurement) {
    if (state.active <= 0) return
    if (measurement.value !== null) {
        window.known = true
        window.peak = Math.max(window.peak, measurement.value)
    }
    if (measurement.status === 'partial' || measurement.status === 'unrecorded') window.missing = true
    if (measurement.status === 'uncapped') window.uncapped = true
}

// Selected-scope request/limit totals for a node: the recorded amounts of the
// sessions placed on it during the window, not whole-worker telemetry.
function aggregateNodeResources(node, group, start, end) {
    const definitions = resourceDefinitions(node, group)
    const states = new Map(
        definitions.map(definition => [
            definition.key,
            {
                requests: newMeasurementState(),
                limits: newMeasurementState(),
                requestsWindow: { known: false, missing: false, peak: 0 },
                limitsWindow: { known: false, missing: false, uncapped: false, peak: 0 },
                points: []
            }
        ])
    )
    const events = new Map()
    const eventAt = at => {
        if (!events.has(at)) events.set(at, { starts: [], ends: [] })
        return events.get(at)
    }

    for (const interval of group.intervals) {
        const values = new Map()
        for (const definition of definitions)
            values.set(definition.key, {
                requests: resourceMetricValue(interval.resources, definition, 'requests'),
                limits: resourceMetricValue(interval.resources, definition, 'limits')
            })
        const segment = { start: interval.start, end: interval.end, values }
        eventAt(interval.start).starts.push(segment)
        if (interval.end < end || interval.endsAtLifetimeBoundary) eventAt(interval.end).ends.push(segment)
    }

    const times = [...new Set([start, end, ...events.keys()])].sort((left, right) => left - right)
    for (const at of times) {
        const event = events.get(at)
        if (event) {
            for (const segment of event.ends)
                for (const definition of definitions) {
                    const values = segment.values.get(definition.key)
                    addMeasurement(states.get(definition.key).requests, values.requests, -1)
                    addMeasurement(states.get(definition.key).limits, values.limits, -1)
                }
            for (const segment of event.starts)
                for (const definition of definitions) {
                    const values = segment.values.get(definition.key)
                    addMeasurement(states.get(definition.key).requests, values.requests, 1)
                    addMeasurement(states.get(definition.key).limits, values.limits, 1)
                }
        }
        for (const definition of definitions) {
            const state = states.get(definition.key)
            const requests = stateMeasurement(state.requests, 'requests')
            const limits = stateMeasurement(state.limits, 'limits')
            state.points.push({
                at,
                requested: requests.value,
                limit: limits.value,
                requestStatus: requests.status,
                limitStatus: limits.status
            })
            recordWindowMeasurement(state.requestsWindow, state.requests, requests)
            recordWindowMeasurement(state.limitsWindow, state.limits, limits)
        }
    }

    return definitions.map(definition => {
        const state = states.get(definition.key)
        const requests = measurementFromFlags(state.requestsWindow.known, state.requestsWindow.missing, false, state.requestsWindow.peak, 'requests')
        const limits = measurementFromFlags(
            state.limitsWindow.known,
            state.limitsWindow.missing,
            state.limitsWindow.uncapped,
            state.limitsWindow.peak,
            'limits'
        )
        return {
            ...definition,
            requested: requests.value,
            limit: limits.value,
            requestStatus: requests.status,
            limitStatus: limits.status,
            points: state.points
        }
    })
}

// Selected-scope placement pass shared by allocationWindow and nodePoolWindow:
// one sweep over overlapping sessions resolves each to the cached node
// lifetimes it covers, and idle cached lifetimes are materialized only when the
// caller asks for pool capacity. Missing or ambiguous placement stays unplaced.
function buildAllocation(snapshot, start, end, rpid, includeIdle) {
    const window = windowBounds(start, end)
    if (!window) return null

    const sessions = Array.isArray(snapshot?.sessions) ? snapshot.sessions : []
    const nodeHistory = Array.isArray(snapshot?.node_history) ? snapshot.node_history : []
    const nodePools = Array.isArray(snapshot?.node_pools) ? snapshot.node_pools : []
    const resourcePools = Array.isArray(snapshot?.resource_pools) ? snapshot.resource_pools : []
    const tenantClusters = Array.isArray(snapshot?.tenant_clusters) ? snapshot.tenant_clusters : []
    const poolMaps = nodePoolMaps(nodePools)
    const poolsById = resourcePoolMap(resourcePools)
    const clustersById = new Map()
    for (const cluster of tenantClusters)
        if (cluster?.tcid !== null && cluster?.tcid !== undefined && !clustersById.has(String(cluster.tcid))) clustersById.set(String(cluster.tcid), cluster)
    const workerIndex = buildWorkerIndex(nodeHistory)
    const nodeGroups = new Map()
    const unplaced = []

    for (const session of sessions) {
        if (rpid !== null && session.rpid !== rpid) continue
        const bounds = intervalBounds(session)
        if (!bounds || bounds.start >= window.end || bounds.stop <= window.start) continue
        const resources = readSessionResources(session)
        const cluster = sessionCluster(session, poolMaps.byShortId)
        const pool = poolMaps.byShortId.get(normalizeNodePool(session.node_pool))
        const sessionStart = Math.max(window.start, bounds.start)
        const sessionEnd = Math.min(window.end, bounds.stop)
        const entries = resolveSessionNodes(workerIndex, session, sessionStart, sessionEnd, cluster, pool)
        if (!entries.length) {
            unplaced.push(session)
            continue
        }

        for (const entry of entries) {
            if (!nodeGroups.has(entry)) {
                nodeGroups.set(entry, {
                    entry,
                    sessions: [],
                    intervals: [],
                    start: Math.max(window.start, entry.start),
                    end: Math.min(window.end, entry.stop)
                })
            }
            const group = nodeGroups.get(entry)
            group.sessions.push(session)
            group.intervals.push({
                start: Math.max(sessionStart, entry.start),
                end: Math.min(sessionEnd, entry.stop),
                endsAtLifetimeBoundary: bounds.stop <= window.end || entry.stop <= window.end,
                resources,
                session
            })
        }
    }

    if (includeIdle) {
        for (const bucket of workerIndex.values())
            for (const entry of bucket.entries) {
                if (nodeGroups.has(entry)) continue
                const groupStart = Math.max(window.start, entry.start)
                const groupEnd = Math.min(window.end, entry.stop)
                if (groupStart >= groupEnd) continue
                nodeGroups.set(entry, { entry, sessions: [], intervals: [], start: groupStart, end: groupEnd, idle: true })
            }
    }

    const allocations = [...nodeGroups.values()].map(group => {
        const { node } = group.entry
        const pool = nodePoolFor(node, group.sessions, poolMaps)
        const identity = nodePoolIdentity(node, pool, group.sessions)
        const cluster = node.tcid === null || node.tcid === undefined ? null : clustersById.get(String(node.tcid))
        const clusterName = cluster?.cluster_name || pool?.cluster_name || node.cluster_name || null
        return {
            node,
            family: nodeFamily(node, pool, group.sessions),
            clusterName,
            gpuMemoryTotal: quantityFromNode(node, 'capacity', GPU_MEMORY_RESOURCE),
            poolNames: uniqueValues(group.sessions.map(session => resourcePoolName(session, poolsById))),
            sessions: group.sessions,
            resources: aggregateNodeResources(node, group, group.start, group.end),
            intervals: group.intervals,
            poolKey: identity.key,
            poolLabel: identity.label,
            sortStart: group.entry.start,
            start: group.start,
            stop: group.end,
            lifetimeEndsAtBoundary: group.entry.stop <= window.end,
            ...(includeIdle ? { idle: group.idle === true } : {})
        }
    })

    const familyOrder = { standard: 0, elastic: 1, integrations: 2 }
    allocations.sort(
        (left, right) =>
            familyOrder[left.family] - familyOrder[right.family] ||
            String(left.clusterName || '').localeCompare(String(right.clusterName || '')) ||
            String(left.node.node_name || '').localeCompare(String(right.node.node_name || '')) ||
            left.sortStart - right.sortStart
    )
    return { window, allocations, unplaced, poolsById, clustersById }
}

function publicAllocation(allocation) {
    const { sortStart, start, stop, lifetimeEndsAtBoundary, intervals, ...rest } = allocation
    return rest
}

/**
 * Build per-node recorded request/limit timelines for a half-open UTC window.
 * Placement is unique by cluster, worker name, pool and node lifetime; missing
 * or ambiguous records stay in `unplaced` rather than being guessed or counted twice.
 */
export function allocationWindow(snapshot, start, end, rpid = null) {
    const model = buildAllocation(snapshot, start, end, rpid, false)
    if (!model) return { nodes: [], unplaced: [] }
    return { nodes: model.allocations.map(publicAllocation), unplaced: model.unplaced }
}

function newMeasurementState() {
    return { sum: 0, known: 0, missing: 0, uncapped: 0, active: 0 }
}

function newLaneState(trackCapacity) {
    return {
        requests: newMeasurementState(),
        limits: newMeasurementState(),
        requestsWindow: { known: false, missing: false, peak: 0 },
        limitsWindow: { known: false, missing: false, uncapped: false, peak: 0 },
        ...(trackCapacity ? { capacity: { sum: 0, known: 0 }, totalCapacity: { sum: 0, known: 0 } } : {}),
        points: []
    }
}

// Pool GPU budgets aggregate raw recorded units: workers with different
// accelerator budgets must never have their normalized percentages added.
function poolResourcePresentation(key, hami) {
    if (key === GPU_CORES_RESOURCE)
        return {
            key,
            label: 'GPU compute (raw recorded budget)',
            unit: 'GPU budget units',
            multiplier: 1,
            note: 'Raw recorded accelerator budget units summed across pool workers; percentages of workers with different budgets are not added together.'
        }
    if (key === GPU_MEMORY_PERCENT_RESOURCE)
        return {
            key,
            label: 'GPU memory allocation (raw recorded budget)',
            unit: 'GPU budget units',
            multiplier: 1,
            note: 'Raw recorded GPU memory budget units summed across pool workers; not percentages of a shared accelerator budget.'
        }
    return { key, ...gpuResourcePresentation(key, hami) }
}

function standardResourceDefinitions() {
    return [
        { key: 'cpu', label: 'CPU', unit: 'vCPU' },
        { key: 'memory', label: 'Memory', unit: 'GB' },
        { key: EPHEMERAL_STORAGE_RESOURCE, label: 'Ephemeral storage', unit: 'GB' }
    ]
}

function appendGpuDefinitions(definitions, gpuKeys, hami) {
    const seen = new Set(definitions.map(definition => definition.key))
    for (const key of [...gpuKeys].sort()) {
        if (seen.has(key)) continue
        seen.add(key)
        definitions.push(poolResourcePresentation(key, hami))
    }
    return definitions
}

function poolResourceDefinitions(entries, intervals) {
    const gpuKeys = new Set()
    for (const entry of entries) for (const source of entry.resources) gpuKeys.add(source.key)
    for (const interval of intervals) for (const key of interval.resources.gpuKeys) gpuKeys.add(key)
    const hami =
        entries.some(entry => isHamiNode(entry.node, nodeResourceKeys(entry.node))) ||
        gpuKeys.has(GPU_MEMORY_RESOURCE) ||
        gpuKeys.has(GPU_MEMORY_PERCENT_RESOURCE) ||
        gpuKeys.has(GPU_CORES_RESOURCE)
    return appendGpuDefinitions(standardResourceDefinitions(), gpuKeys, hami)
}

function addCapacity(state, value, direction) {
    if (value === null || value === undefined) return
    state.sum = cleanSum(state.sum + value * direction)
    state.known += direction
}

// Summed allocatable of the node lifetimes active at one instant. Known values
// are summed even when other active lifetimes have no recorded value, but the
// result is then only a `partial` (≥ known) lower bound; a pool that loaded no
// worker history stays `unrecorded` instead of advertising zero capacity.
function poolCapacityMeasurement(state, activeLifetimes, hasWorkers) {
    if (!hasWorkers) return measurement(null, 'unrecorded')
    if (activeLifetimes <= 0) return measurement(0, 'exact')
    if (state.known <= 0) return measurement(null, 'unrecorded')
    return measurement(cleanSum(state.sum), state.known < activeLifetimes ? 'partial' : 'exact')
}

// Window summary for a per-point capacity series: the peak recorded value, with
// `partial` whenever any interval was a lower bound.
function capacitySummary(points, field, statusField) {
    let value = null
    let partial = false
    for (let index = 0; index < points.length - 1; index++) {
        const point = points[index]
        if (point[statusField] !== 'exact') partial = true
        const pointValue = point[field]
        if (pointValue === null || pointValue === undefined) continue
        value = value === null ? pointValue : Math.max(value, pointValue)
    }
    if (value === null) return measurement(null, 'unrecorded')
    return measurement(value, partial ? 'partial' : 'exact')
}

// One sweep per pool: every session boundary, node lifetime boundary and window
// edge is a point. Contributions accumulate concurrently while capacity
// accumulates the node lifetimes active at the same instant, and each space
// (sid) is updated in the same pass instead of rescanning per class.
function buildPoolTimelines(entries, definitions, window, spaceLabels, usageIntervals) {
    const times = new Set([window.start, window.end])
    const events = new Map()
    const eventAt = at => {
        let event = events.get(at)
        if (!event) {
            event = { starts: [], ends: [], capacityStarts: [], capacityEnds: [] }
            events.set(at, event)
        }
        return event
    }
    const spaceStates = new Map()
    const spaceFor = session => {
        const sid = session?.sid
        const recorded = sid !== null && sid !== undefined && sid !== ''
        const key = recorded ? `sid:${identifierKey(sid)}` : 'sid:not-recorded'
        let state = spaceStates.get(key)
        if (!state) {
            state = {
                key,
                sid: recorded ? sid : null,
                label: recorded ? spaceLabels.get(identifierKey(sid)) || `Space ${sid}` : 'Space not recorded',
                sessions: new Set(),
                active: 0,
                lanes: definitions.map(() => newLaneState(false))
            }
            spaceStates.set(key, state)
        }
        return state
    }

    for (const entry of entries) {
        if (!(entry.start < entry.stop)) continue
        const capacity = definitions.map(definition => quantityFromNode(entry.node, 'allocatable', definition.key))
        const totalCapacity = definitions.map(definition => quantityFromNode(entry.node, 'capacity', definition.key))
        times.add(entry.start)
        eventAt(entry.start).capacityStarts.push({ capacity, totalCapacity })
        if (entry.stop < window.end || entry.lifetimeEndsAtBoundary) {
            times.add(entry.stop)
            eventAt(entry.stop).capacityEnds.push({ capacity, totalCapacity })
        }
    }
    for (const interval of usageIntervals) {
        const values = definitions.map(definition => {
            const summary = sessionSummary(interval.resources, definition.key)
            return { requests: summary.requests, limits: summary.limits }
        })
        const space = spaceFor(interval.session)
        space.sessions.add(interval.session)
        times.add(interval.start)
        eventAt(interval.start).starts.push({ values, space })
        if (interval.end < window.end || interval.endsAtLifetimeBoundary) {
            times.add(interval.end)
            eventAt(interval.end).ends.push({ values, space })
        }
    }

    const lanes = definitions.map(() => newLaneState(true))
    const hasWorkers = entries.length > 0
    let activeLifetimes = 0
    for (const at of [...times].sort((left, right) => left - right)) {
        const event = events.get(at)
        const touched = new Set()
        if (event) {
            for (const item of event.ends) {
                lanes.forEach((lane, index) => {
                    addMeasurement(lane.requests, item.values[index].requests, -1)
                    addMeasurement(lane.limits, item.values[index].limits, -1)
                    addMeasurement(item.space.lanes[index].requests, item.values[index].requests, -1)
                    addMeasurement(item.space.lanes[index].limits, item.values[index].limits, -1)
                })
                item.space.active -= 1
                touched.add(item.space)
            }
            for (const item of event.capacityEnds) {
                activeLifetimes -= 1
                lanes.forEach((lane, index) => {
                    addCapacity(lane.capacity, item.capacity[index], -1)
                    addCapacity(lane.totalCapacity, item.totalCapacity[index], -1)
                })
            }
            for (const item of event.starts) {
                lanes.forEach((lane, index) => {
                    addMeasurement(lane.requests, item.values[index].requests, 1)
                    addMeasurement(lane.limits, item.values[index].limits, 1)
                    addMeasurement(item.space.lanes[index].requests, item.values[index].requests, 1)
                    addMeasurement(item.space.lanes[index].limits, item.values[index].limits, 1)
                })
                item.space.active += 1
                touched.add(item.space)
            }
            for (const item of event.capacityStarts) {
                activeLifetimes += 1
                lanes.forEach((lane, index) => {
                    addCapacity(lane.capacity, item.capacity[index], 1)
                    addCapacity(lane.totalCapacity, item.totalCapacity[index], 1)
                })
            }
        }
        const capacityAt = lanes.map(lane => poolCapacityMeasurement(lane.capacity, activeLifetimes, hasWorkers))
        const totalCapacityAt = lanes.map(lane => poolCapacityMeasurement(lane.totalCapacity, activeLifetimes, hasWorkers))
        lanes.forEach((lane, index) => {
            const requests = stateMeasurement(lane.requests, 'requests')
            const limits = stateMeasurement(lane.limits, 'limits')
            lane.points.push({
                at,
                requested: requests.value,
                limit: limits.value,
                requestStatus: requests.status,
                limitStatus: limits.status,
                capacity: capacityAt[index].value,
                capacityStatus: capacityAt[index].status,
                totalCapacity: totalCapacityAt[index].value,
                totalCapacityStatus: totalCapacityAt[index].status
            })
            recordWindowMeasurement(lane.requestsWindow, lane.requests, requests)
            recordWindowMeasurement(lane.limitsWindow, lane.limits, limits)
        })
        if (at === window.start || at === window.end || event?.capacityStarts.length || event?.capacityEnds.length) {
            for (const state of spaceStates.values()) touched.add(state)
        }
        for (const space of touched)
            space.lanes.forEach((lane, index) => {
                const requests = stateMeasurement(lane.requests, 'requests')
                const limits = stateMeasurement(lane.limits, 'limits')
                lane.points.push({
                    at,
                    requested: requests.value,
                    limit: limits.value,
                    requestStatus: requests.status,
                    limitStatus: limits.status,
                    capacity: capacityAt[index].value,
                    capacityStatus: capacityAt[index].status,
                    totalCapacity: totalCapacityAt[index].value,
                    totalCapacityStatus: totalCapacityAt[index].status
                })
                recordWindowMeasurement(lane.requestsWindow, lane.requests, requests)
                recordWindowMeasurement(lane.limitsWindow, lane.limits, limits)
            })
    }

    const resources = lanes.map((lane, index) => {
        const requests = measurementFromFlags(lane.requestsWindow.known, lane.requestsWindow.missing, false, lane.requestsWindow.peak, 'requests')
        const limits = measurementFromFlags(lane.limitsWindow.known, lane.limitsWindow.missing, lane.limitsWindow.uncapped, lane.limitsWindow.peak, 'limits')
        const capacity = capacitySummary(lane.points, 'capacity', 'capacityStatus')
        const totalCapacity = capacitySummary(lane.points, 'totalCapacity', 'totalCapacityStatus')
        return {
            ...definitions[index],
            requested: requests.value,
            limit: limits.value,
            requestStatus: requests.status,
            limitStatus: limits.status,
            capacity: capacity.value,
            capacityStatus: capacity.status,
            totalCapacity: totalCapacity.value,
            totalCapacityStatus: totalCapacity.status,
            points: lane.points
        }
    })
    const spaces = [...spaceStates.values()].map(state => ({
        key: state.key,
        sid: state.sid,
        label: state.label,
        resources: state.lanes.map((lane, index) => {
            const requests = measurementFromFlags(lane.requestsWindow.known, lane.requestsWindow.missing, false, lane.requestsWindow.peak, 'requests')
            const limits = measurementFromFlags(
                lane.limitsWindow.known,
                lane.limitsWindow.missing,
                lane.limitsWindow.uncapped,
                lane.limitsWindow.peak,
                'limits'
            )
            return {
                ...definitions[index],
                requested: requests.value,
                limit: limits.value,
                requestStatus: requests.status,
                limitStatus: limits.status,
                capacity: resources[index].capacity,
                capacityStatus: resources[index].capacityStatus,
                totalCapacity: resources[index].totalCapacity,
                totalCapacityStatus: resources[index].totalCapacityStatus,
                points: lane.points
            }
        }),
        sessionCount: state.sessions.size
    }))
    spaces.sort(
        (left, right) =>
            right.sessionCount - left.sessionCount || String(left.label).localeCompare(String(right.label)) || String(left.key).localeCompare(String(right.key))
    )
    return { resources, spaces }
}

function dominantFamily(entries) {
    const counts = new Map()
    for (const entry of entries) counts.set(entry.family, (counts.get(entry.family) || 0) + 1)
    let family = entries[0].family
    let best = counts.get(family) || 0
    for (const [candidate, count] of counts)
        if (count > best) {
            family = candidate
            best = count
        }
    return family
}

function poolUsageIntervals(entries, unplaced, window, fullSessions) {
    const bySession = new Map()
    for (const entry of entries)
        for (const interval of entry.intervals) {
            if (!bySession.has(interval.session)) bySession.set(interval.session, [])
            bySession.get(interval.session).push(interval)
        }
    for (const session of unplaced) if (!bySession.has(session)) bySession.set(session, [])
    const result = []
    for (const [session, intervals] of bySession) {
        if (intervals.length && !fullSessions.has(session)) {
            result.push(...intervals)
            continue
        }
        const bounds = intervalBounds(session)
        if (!bounds) continue
        const start = Math.max(window.start, bounds.start)
        const end = Math.min(window.end, bounds.stop)
        if (start >= end) continue
        result.push({
            session,
            start,
            end,
            resources: intervals.length ? intervals[0].resources : readSessionResources(session),
            endsAtLifetimeBoundary: bounds.stop <= window.end
        })
    }
    return result
}

function buildPool(key, entries, window, attributedUnplaced, spaceLabels, fullSessions, poolsById) {
    const intervals = poolUsageIntervals(entries, attributedUnplaced, window, fullSessions)
    const definitions = poolResourceDefinitions(entries, intervals)
    const { resources, spaces } = buildPoolTimelines(entries, definitions, window, spaceLabels, intervals)
    const sessions = new Set()
    for (const entry of entries) for (const session of entry.sessions) sessions.add(session)
    for (const session of attributedUnplaced) sessions.add(session)
    return {
        key,
        label: entries[0].poolLabel,
        family: dominantFamily(entries),
        clusterName: entries.find(entry => entry.clusterName)?.clusterName || null,
        poolNames: uniqueValues([...entries.flatMap(entry => entry.poolNames), ...attributedUnplaced.map(session => resourcePoolName(session, poolsById))]),
        nodes: entries.map(publicAllocation),
        resources,
        spaces,
        sessionCount: sessions.size,
        unplacedCount: attributedUnplaced.length,
        unplaced: attributedUnplaced
    }
}

// A pool can have recorded usage without a resolved worker. Include that usage
// and its class contributions while leaving absent worker capacity unrecorded.
function buildUnplacedPool(identity, sessions, window, spaceLabels, clusterName, poolsById) {
    const intervals = poolUsageIntervals([], sessions, window, new Set())
    const definitions = poolResourceDefinitions([], intervals)
    const { resources, spaces } = buildPoolTimelines([], definitions, window, spaceLabels, intervals)
    return {
        key: identity.key,
        label: identity.label,
        family: identity.family,
        clusterName,
        poolNames: uniqueValues(sessions.map(session => resourcePoolName(session, poolsById))),
        nodes: [],
        resources,
        spaces,
        sessionCount: sessions.length,
        unplacedCount: sessions.length,
        unplaced: sessions
    }
}

/**
 * Pool-scoped timelines for a half-open UTC window. Nodes group by stable
 * cluster + node-pool identity (idle cached lifetimes included); totals are
 * concurrent session amounts in pool units, capacity is the summed allocatable
 * of the node lifetimes active at the same instant. Spaces group by recorded
 * sid; uniquely attributable usage remains in the pool even if its worker is
 * unresolved. Everything describes the loaded worker histories in the selected
 * billing scope, not claimed exhaustive infrastructure telemetry.
 */
export function nodePoolWindow(snapshot, start, end, rpid = null) {
    const model = buildAllocation(snapshot, start, end, rpid, true)
    if (!model) return { pools: [], unplaced: [] }
    const entriesByPool = new Map()
    const sessionPools = new Map()
    for (const allocation of model.allocations) {
        if (!entriesByPool.has(allocation.poolKey)) entriesByPool.set(allocation.poolKey, [])
        entriesByPool.get(allocation.poolKey).push(allocation)
        for (const session of allocation.sessions) {
            if (!sessionPools.has(session)) sessionPools.set(session, new Set())
            sessionPools.get(session).add(allocation.poolKey)
        }
    }
    const fullSessions = new Set()
    for (const [session, keys] of sessionPools) {
        const key = [...keys][0]
        if (keys.size === 1 && !key.startsWith('node:') && !key.includes('|unrecorded|')) fullSessions.add(session)
    }
    const shortIdIndex = nodePoolShortIdIndex(Array.isArray(snapshot?.node_pools) ? snapshot.node_pools : [])
    const attributed = new Map()
    for (const session of model.unplaced) {
        const identity = sessionPoolIdentity(session, shortIdIndex)
        if (!identity) continue
        if (!attributed.has(identity.key)) attributed.set(identity.key, { identity, sessions: [] })
        attributed.get(identity.key).sessions.push(session)
    }
    const spaceLabels = spaceLabelIndex(snapshot)
    const pools = [...entriesByPool.entries()].map(([key, entries]) =>
        buildPool(key, entries, model.window, attributed.get(key)?.sessions || [], spaceLabels, fullSessions, model.poolsById)
    )
    for (const [key, group] of attributed) {
        if (entriesByPool.has(key)) continue
        const cluster = group.identity.tcid === null || group.identity.tcid === undefined ? null : model.clustersById.get(String(group.identity.tcid))
        const clusterName = cluster?.cluster_name || group.identity.clusterName || null
        pools.push(buildUnplacedPool(group.identity, group.sessions, model.window, spaceLabels, clusterName, model.poolsById))
    }
    const familyOrder = { standard: 0, elastic: 1, integrations: 2 }
    pools.sort(
        (left, right) =>
            familyOrder[left.family] - familyOrder[right.family] ||
            String(left.clusterName || '').localeCompare(String(right.clusterName || '')) ||
            String(left.label || '').localeCompare(String(right.label || '')) ||
            String(left.key).localeCompare(String(right.key))
    )
    return { pools, unplaced: model.unplaced }
}

/**
 * Recorded reservations per billing resource pool for a half-open UTC window,
 * grouped by the pool each session was billed to (`rpid`) exactly once.
 *
 * Requests/limits are the pointwise concurrent peaks of the sessions billed to
 * a pool, so a direct row never adds an unrelated pool's peaks. A billed pool
 * carries no capacity: the workers a session ran on belong to the node pools,
 * not to the pool that paid for the session, so capacity stays unrecorded here.
 * The selected pool's subtree summary and each parent's subtree row are
 * aggregated over their own concurrent sessions; summing the direct rows'
 * separate peaks would overstate usage across pools.
 * Idle descendants are omitted from the breakdown; a parent remains visible
 * when its own sessions are absent but a descendant has activity.
 */
export function resourcePoolBilling(snapshot, start, end, rpid = null) {
    const window = windowBounds(start, end)
    const rootRpid = rpid === null || rpid === undefined ? null : identifierKey(rpid)
    if (!window || rootRpid === null) return null

    const sessions = Array.isArray(snapshot?.sessions) ? snapshot.sessions : []
    const pools = Array.isArray(snapshot?.resource_pools) ? snapshot.resource_pools : []
    const spaceLabels = spaceLabelIndex(snapshot)

    const poolByRpid = new Map()
    for (const pool of pools) {
        const key = identifierKey(pool?.rpid)
        if (key !== null && !poolByRpid.has(key)) poolByRpid.set(key, pool)
    }
    const childrenOf = new Map()
    for (const [key, pool] of poolByRpid) {
        const parent = identifierKey(pool.parent_rpid)
        if (parent === null || parent === key) continue
        if (!childrenOf.has(parent)) childrenOf.set(parent, [])
        childrenOf.get(parent).push(key)
    }

    // Group overlapping sessions by billed pool, parsing each session's recorded
    // resource_config once. A session with no recorded pool cannot be assigned to
    // an entity, so it is only counted.
    const intervalsByRpid = new Map()
    let sessionCount = 0
    let unattributedSessions = 0
    for (const session of sessions) {
        const bounds = intervalBounds(session)
        if (!bounds) continue
        const from = Math.max(window.start, bounds.start)
        const to = Math.min(window.end, bounds.stop)
        if (from >= to) continue
        sessionCount += 1
        const key = identifierKey(session.rpid)
        if (key === null) {
            unattributedSessions += 1
            continue
        }
        if (!intervalsByRpid.has(key)) intervalsByRpid.set(key, [])
        intervalsByRpid.get(key).push({
            session,
            start: from,
            end: to,
            resources: readSessionResources(session),
            endsAtLifetimeBoundary: bounds.stop <= window.end
        })
    }

    const scopedIntervals = []
    for (const group of intervalsByRpid.values()) scopedIntervals.push(...group)
    const definitions = poolResourceDefinitions([], scopedIntervals)
    const emptySummary = () => ({
        sessionCount: 0,
        resources: definitions.map(definition => ({
            ...definition,
            requested: null,
            requestStatus: 'unrecorded',
            limit: null,
            limitStatus: 'unrecorded'
        }))
    })
    const summarize = intervals =>
        intervals.length
            ? { sessionCount: intervals.length, resources: buildPoolTimelines([], definitions, window, spaceLabels, intervals).resources }
            : emptySummary()

    const subtreeIntervals = new Map()
    const intervalsForSubtree = key => {
        if (subtreeIntervals.has(key)) return subtreeIntervals.get(key)
        const collected = (intervalsByRpid.get(key) || []).slice()
        subtreeIntervals.set(key, collected)
        for (const child of childrenOf.get(key) || []) collected.push(...intervalsForSubtree(child))
        return collected
    }

    const rows = []
    const visited = new Set()
    const visit = (key, depth) => {
        if (visited.has(key)) return
        const subtree = intervalsForSubtree(key)
        if (key !== rootRpid && subtree.length === 0) return
        visited.add(key)
        const pool = poolByRpid.get(key) || {}
        const children = (childrenOf.get(key) || [])
            .filter(child => intervalsForSubtree(child).length > 0)
            .sort((left, right) => Number(left) - Number(right) || String(left).localeCompare(String(right)))
        rows.push({
            rpid: key,
            label: pool.long_id || pool.name || `Resource pool ${key}`,
            parentRpid: identifierKey(pool.parent_rpid),
            depth,
            isSelected: key === rootRpid,
            childCount: children.length,
            direct: summarize(intervalsByRpid.get(key) || []),
            subtree: children.length ? summarize(subtree) : null
        })
        for (const child of children) visit(child, depth + 1)
    }
    visit(rootRpid, 0)
    // A descendant whose hierarchy row was not returned still carries allocation:
    // show it at the top level rather than silently dropping its sessions.
    for (const key of poolByRpid.keys()) if (!visited.has(key)) visit(key, 1)

    return {
        rootRpid,
        rootLabel: poolByRpid.get(rootRpid)?.long_id || `Resource pool ${rootRpid}`,
        window,
        resources: definitions,
        rows,
        subtree: rows[0].subtree || rows[0].direct,
        sessionCount,
        unattributedSessions
    }
}
